/**
 * Phase 298 — Academy Owner Recovery Inbox
 *
 * Lives at `/recovery-inbox`. Shows every 1-3★ private parent review with
 * the AI-drafted recovery email (editable) + the suggested internal task.
 * Owner clicks "Send Email" → fires Resend → flips status to 'sent'.
 *
 * Tabs:
 *   • Pending review (default)  — needs the owner's eyes
 *   • Sent                      — historical archive of recovery emails
 *   • Ignored                   — dismissed without sending
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Mail, Send, Loader2, RefreshCcw, AlertTriangle, ClipboardList,
  Sparkles, X, MessageCircle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending_review', label: 'Pending review', icon: Mail },
  { value: 'sent',           label: 'Sent',           icon: CheckCircle2 },
  { value: 'ignored',        label: 'Ignored',        icon: X },
];

function StarRow({ rating }) {
  return (
    <span className="text-amber-500 tracking-wider font-medium tabular-nums" aria-label={`${rating} stars`}>
      {'★'.repeat(rating)}<span className="text-slate-300">{'★'.repeat(Math.max(0, 5 - rating))}</span>
    </span>
  );
}

function InboxCard({ item, onSent, onIgnored }) {
  const [subject, setSubject] = useState(item.ai_email_subject || item.recovery_sent_subject || '');
  const [body, setBody] = useState(item.ai_draft_response || item.recovery_sent_response || '');
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  const isPending = item.recovery_status === 'pending_review';
  const isSent = item.recovery_status === 'sent';
  const isIgnored = item.recovery_status === 'ignored';
  const aiFailed = item.ai_draft_status === 'failed';
  const aiPending = !aiFailed && !item.ai_draft_response && isPending;

  const handleSend = useCallback(async () => {
    if (!body.trim()) { toast.error('Email body is empty'); return; }
    setSending(true);
    try {
      await api.post(`/recovery-inbox/${item.id}/send`, {
        edited_subject: subject,
        edited_response: body,
      });
      toast.success('Recovery email sent');
      onSent(item.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  }, [body, subject, item.id, onSent]);

  const handleIgnore = useCallback(async () => {
    setIgnoring(true);
    try {
      await api.post(`/recovery-inbox/${item.id}/ignore`);
      toast.success('Item moved to Ignored');
      onIgnored(item.id);
    } catch (err) {
      toast.error('Could not ignore');
    } finally {
      setIgnoring(false);
    }
  }, [item.id, onIgnored]);

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/recovery-inbox/${item.id}/regenerate`);
      if (r.data?.ai_draft_response) {
        setBody(r.data.ai_draft_response);
        setSubject(r.data.ai_email_subject || subject);
        toast.success('Fresh AI draft generated');
      } else {
        toast.error('AI is unavailable right now — please try again');
      }
    } catch (err) {
      toast.error('Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  }, [item.id, subject]);

  return (
    <Card className="border-l-4 border-l-rose-300" data-testid={`recovery-card-${item.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StarRow rating={item.rating} />
              <span className="text-sm font-semibold text-slate-900" data-testid={`recovery-parent-name-${item.id}`}>
                {item.parent_name || 'Anonymous Parent'}
              </span>
              {item.student_name && (
                <Badge variant="secondary" className="text-[10px]">{item.student_name}</Badge>
              )}
              {item.student_batch && (
                <Badge variant="outline" className="text-[10px]">{item.student_batch}</Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {item.parent_email || 'No email on file'}
              {item.responded_at && ` · ${new Date(item.responded_at).toLocaleString()}`}
            </p>
          </div>
          {isSent && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Sent</Badge>}
          {isIgnored && <Badge variant="secondary">Ignored</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Parent's verbatim complaint */}
        {item.comments && (
          <blockquote className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-slate-800" data-testid={`recovery-comment-${item.id}`}>
            <p className="text-[10px] uppercase tracking-wider text-rose-600 font-bold mb-1">Parent&apos;s comment</p>
            <p className="whitespace-pre-wrap leading-relaxed">{item.comments}</p>
          </blockquote>
        )}

        {/* Internal action task (read-only) */}
        {item.recovery_task && (
          <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3" data-testid={`recovery-task-${item.id}`}>
            <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold mb-1 flex items-center gap-1">
              <ClipboardList className="h-3 w-3" /> Internal action
            </p>
            <p className="text-sm text-slate-900">{item.recovery_task}</p>
          </div>
        )}

        {/* AI draft email — editable when pending, read-only when sent/ignored */}
        {aiPending ? (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center text-sm text-slate-500" data-testid={`recovery-ai-pending-${item.id}`}>
            <Loader2 className="h-4 w-4 inline animate-spin mr-1.5" />
            Claude is drafting your recovery email…
          </div>
        ) : aiFailed && !body ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm" data-testid={`recovery-ai-failed-${item.id}`}>
            <p className="font-semibold text-amber-800 flex items-center gap-1 mb-1"><AlertTriangle className="h-3.5 w-3.5" /> AI draft unavailable</p>
            <p className="text-xs text-amber-700">Compose your own response below, or click <strong>Regenerate</strong> to retry.</p>
          </div>
        ) : null}

        {(isPending || isSent || isIgnored) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-emerald-600" />
                AI Recovery Draft
                {item.ai_model_used && <Badge variant="outline" className="text-[9px] ml-1 font-normal">{item.ai_model_used}</Badge>}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-slate-600">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={!isPending || sending}
                placeholder="Subject line"
                data-testid={`recovery-subject-input-${item.id}`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-slate-600">Body</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={!isPending || sending}
                rows={9}
                placeholder="Write your response to the parent…"
                className="font-mono text-[13px] leading-relaxed"
                data-testid={`recovery-body-textarea-${item.id}`}
              />
            </div>
          </div>
        )}

        {/* Action row — only for pending items */}
        {isPending && (
          <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRegenerate}
              disabled={regenerating || sending}
              className="text-xs"
              data-testid={`recovery-regenerate-btn-${item.id}`}
            >
              {regenerating
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Regenerating…</>
                : <><RefreshCcw className="h-3.5 w-3.5 mr-1" /> Regenerate</>}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleIgnore}
                disabled={ignoring || sending}
                className="text-xs"
                data-testid={`recovery-ignore-btn-${item.id}`}
              >
                {ignoring ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <X className="h-3.5 w-3.5 mr-1" />}
                Ignore
              </Button>
              <Button
                size="sm"
                onClick={handleSend}
                disabled={sending || !body.trim() || !item.parent_email}
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`recovery-send-btn-${item.id}`}
              >
                {sending
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Sending…</>
                  : <><Send className="h-3.5 w-3.5 mr-1" /> Send Email</>}
              </Button>
            </div>
          </div>
        )}
        {isSent && item.recovery_sent_at && (
          <p className="text-[11px] text-emerald-700 pt-1">
            ✓ Sent {new Date(item.recovery_sent_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function RecoveryInbox() {
  const [status, setStatus] = useState('pending_review');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInbox = useCallback(async (s) => {
    setLoading(true);
    try {
      const r = await api.get('/recovery-inbox', { params: { status: s } });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error('Could not load recovery inbox');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInbox(status); }, [status, fetchInbox]);

  const handleSent = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);
  const handleIgnored = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const counts = useMemo(() => ({
    pending: items.filter((x) => x.recovery_status === 'pending_review').length,
    sent: items.filter((x) => x.recovery_status === 'sent').length,
    ignored: items.filter((x) => x.recovery_status === 'ignored').length,
  }), [items]);

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="recovery-inbox-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-rose-500" />
            Reputation Recovery Inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Private 1-3★ parent feedback with Claude-drafted recovery emails. Review, edit, and send — turn detractors into promoters.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchInbox(status)} data-testid="recovery-refresh-btn">
          <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="grid w-full max-w-md grid-cols-3" data-testid="recovery-status-tabs">
          {STATUS_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.value} value={t.value} className="text-xs" data-testid={`recovery-tab-${t.value}`}>
                <Icon className="h-3.5 w-3.5 mr-1" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {STATUS_TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="space-y-3 mt-3">
            {loading && (
              <>
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </>
            )}
            {!loading && items.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid={`recovery-empty-state-${t.value}`}>
                  {t.value === 'pending_review' && (
                    <>
                      <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
                      <p className="font-semibold text-slate-900">Inbox zero!</p>
                      <p>No private 1-3★ feedback waiting — your academy is making parents happy.</p>
                    </>
                  )}
                  {t.value === 'sent' && <p>No recovery emails sent yet.</p>}
                  {t.value === 'ignored' && <p>No ignored items.</p>}
                </CardContent>
              </Card>
            )}
            {!loading && items.map((item) => (
              <InboxCard key={item.id} item={item} onSent={handleSent} onIgnored={handleIgnored} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
